import Phaser from "phaser";
import type { GameApp } from "../app/GameApp";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";
import { forgeUpgradeCost, getCard, type CardDef } from "../core/roscha";

const RARITY_LABEL: Record<CardDef["rarity"], string> = {
  common: "Обычная",
  uncommon: "Необычная",
  rare: "Редкая",
  epic: "Эпическая",
};

export class ForgeOverlay extends Phaser.Scene {
  private app!: GameApp;
  private overlay?: DomOverlay;
  private unsubWorld?: () => void;
  private onWake = () => this.refresh();

  constructor() {
    super("ForgeOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.mount();
    this.refresh();
    this.unsubWorld = this.app.onWorldState(() => this.refresh());
    this.events.on(Phaser.Scenes.Events.WAKE, this.onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.WAKE, this.onWake);
      this.unsubWorld?.();
      this.unsubWorld = undefined;
      this.overlay = undefined;
    });
  }

  private mount() {
    const bodyHtml = `
      <div class="dom-resource-row">
        <span><span class="icon-gold"></span> <strong data-bind="gold">0</strong></span>
        <span>Колода <strong data-bind="deck-count">0</strong></span>
      </div>
      <p class="dom-section__desc">
        Выбери карту, чтобы получить её усиленную версию (+2 base, +0.5×stat). Эпические карты улучшать нельзя.
      </p>
      <div class="forge__cards" data-bind="cards"></div>
    `;

    this.overlay = mountOverlay(this, {
      title: "Кузница",
      subtitle: "Улучшай свои карты",
      bodyHtml,
      onClose: () => this.app.openTown(),
      inBody: true,
    });

    this.overlay.root.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-upgrade-card]");
      if (!btn || btn.disabled) return;
      const cardId = btn.dataset.upgradeCard;
      const cost = Number(btn.dataset.cost);
      if (cardId && cost > 0) this.app.upgradeDeckCard(cardId, cost);
    });
  }

  private refresh() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero || !this.overlay) return;

    const gold = hero.inventory.resources.gold ?? 0;
    const goldEl = this.overlay.root.querySelector<HTMLElement>('[data-bind="gold"]');
    const deckEl = this.overlay.root.querySelector<HTMLElement>('[data-bind="deck-count"]');
    if (goldEl) goldEl.textContent = String(gold);
    if (deckEl) deckEl.textContent = String(hero.deck.length);

    const cardsEl = this.overlay.root.querySelector<HTMLElement>('[data-bind="cards"]');
    if (!cardsEl) return;

    const uniqueIds = Array.from(new Set(hero.deck));
    const rows = uniqueIds.map((id) => this.renderCardRow(id, gold, hero.reputationUpgrades));
    cardsEl.innerHTML = rows.length === 0
      ? `<p class="dom-empty">Колода пуста.</p>`
      : rows.join("");
  }

  private renderCardRow(cardId: string, gold: number, repUpgrades: Record<string, number>): string {
    let card: CardDef;
    try {
      card = getCard(cardId);
    } catch {
      return "";
    }
    const isUpgraded = cardId.endsWith("_plus");
    const isEpic = card.rarity === "epic";
    const cost = forgeUpgradeCost(card.rarity, repUpgrades);
    const canUpgrade = !isUpgraded && !isEpic && gold >= cost;
    const disabledLabel = isUpgraded ? "Уже улучшена" : isEpic ? "Эпические нельзя" : gold < cost ? `Нужно ${cost} <span class="icon-gold icon-gold--sm"></span>` : "";
    return `
      <div class="forge__card">
        <div class="forge__card-info">
          <span class="forge__card-name">${card.name}</span>
          <span class="forge__card-meta"><span class="dom-tag dom-tag--${card.rarity}">${RARITY_LABEL[card.rarity]}</span></span>
        </div>
        <button class="dom-btn ${canUpgrade ? "dom-btn--success" : ""}"
                data-upgrade-card="${cardId}" data-cost="${cost}"
                ${canUpgrade ? "" : "disabled"}>
          ${canUpgrade ? `Улучшить · ${cost} <span class="icon-gold icon-gold--sm"></span>` : disabledLabel}
        </button>
      </div>
    `;
  }
}
